import { useEffect, useRef, useState } from "react";
import { Loader2, Upload, FileText, Eye } from "lucide-react";
import { toast } from "sonner";
import { adminApi, fmtErr, fmtDate } from "../api";
import { Btn, Field, inputCls } from "../components";
import { uploadDoc } from "./CertsAdmin";

const CompanyProfileAdmin = () => {
  const [profile, setProfile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    adminApi.get("/company-profile").then((r) => setProfile(r.data)).catch((e) => toast.error(fmtErr(e)));
  }, []);

  if (!profile) return <div className="py-20 text-center text-slate-400"><Loader2 className="animate-spin mx-auto" /></div>;

  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Company profile must be a PDF");
      return;
    }
    setUploading(true);
    try {
      const r = await uploadDoc(f);
      setProfile({ ...profile, doc_url: r.url });
      toast.success("PDF uploaded — click Save & Publish to make it live");
    } catch (err) { toast.error(fmtErr(err)); } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async (active) => {
    setBusy(true);
    try {
      await adminApi.put("/company-profile", { title: profile.title, version: profile.version, doc_url: profile.doc_url, active });
      setProfile({ ...profile, active });
      toast.success(active ? "Company Profile is now live on the website" : "Company Profile unpublished");
    } catch (err) { toast.error(fmtErr(err)); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl" data-testid="admin-company-profile-page">
      <h1 className="font-display text-2xl font-bold text-slate-900">Company Profile PDF</h1>
      <p className="mt-1 text-sm text-slate-500">One profile is publicly active at a time. Replacing the PDF here updates the website automatically — no rebuild needed.</p>

      <div className="mt-6 bg-white border border-slate-200 rounded-sm p-6 space-y-5">
        <div className="flex items-center justify-between">
          <span className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 border rounded-sm ${profile.active ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-slate-200 text-slate-500 border-slate-300"}`} data-testid="profile-status">
            {profile.active ? "Live on website" : "Not published"}
          </span>
          {profile.doc_url && (
            <a href={profile.doc_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-[#C5221F] hover:underline" data-testid="profile-view-link">
              <Eye size={13} /> View current PDF
            </a>
          )}
        </div>
        <Field label="Document Title">
          <input className={inputCls} value={profile.title} onChange={(e) => setProfile({ ...profile, title: e.target.value })} data-testid="profile-title-input" />
        </Field>
        <Field label="Version / Date" hint='e.g. "October 2025" or "v3.1"'>
          <input className={inputCls} value={profile.version} onChange={(e) => setProfile({ ...profile, version: e.target.value })} data-testid="profile-version-input" />
        </Field>
        <Field label="Profile PDF (max 15 MB)">
          <input ref={fileRef} type="file" accept=".pdf" className="hidden" onChange={pick} data-testid="profile-file-input" />
          <div className={`border-2 border-dashed rounded-sm px-4 py-6 flex items-center justify-center gap-3 text-sm ${profile.doc_url ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-slate-300 text-slate-400"}`}>
            <FileText size={18} />
            {profile.doc_url ? "A PDF is attached. Upload a new one to replace it." : "No PDF attached yet."}
          </div>
          <div className="mt-3">
            <Btn variant="ghost" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="profile-upload-btn">
              {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} {profile.doc_url ? "Replace PDF" : "Upload PDF"}
            </Btn>
          </div>
        </Field>
        <div className="flex justify-end gap-3 border-t border-slate-100 pt-4">
          {profile.active && <Btn variant="dark" onClick={() => save(false)} disabled={busy} data-testid="profile-unpublish-btn">Unpublish</Btn>}
          <Btn onClick={() => save(true)} disabled={busy} data-testid="profile-publish-btn">{busy && <Loader2 size={13} className="animate-spin" />} Save &amp; Publish</Btn>
        </div>
      </div>
    </div>
  );
};

export default CompanyProfileAdmin;
